// studio/PreviewPanel.jsx — Aperçu device (Mobile/Tablette/Desktop) + Media Studio (vidéo simulée).
import { useRef, useState } from "react";
import { ST, PNL, tabS, chip } from "@/internal/studio/theme";
import { newComponentLabel } from "@/internal/studio/simEngine";
import { ArrowLeft, Search, Menu, Car, MapPin, Plane, Users, Bike, Bus, Ship, Clapperboard, Play } from "lucide-react";

const DEVICES = { Mobile: 248, Tablette: 330, Desktop: "100%" };
const MAIN = [
  { l: "Taxi", c: "#F59E0B", I: Car }, { l: "Location", c: "#2EE27B", I: MapPin }, { l: "Avion", c: "#A78BFA", I: Plane },
];
const SEC = [{ l: "Tricycle", I: Bike }, { l: "Moto", I: Bike }, { l: "Bus", I: Bus }, { l: "Bâteau", I: Ship }];

export function DevicePreview({ build, phase }) {
  const [device, setDevice] = useState("Mobile");
  const showNew = phase && phase !== "plan" && phase !== "coding";
  const label = newComponentLabel(build);
  return (
    <section style={{ ...PNL, flex: 1.25 }} data-testid="studio-preview-panel">
      <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${ST.border}`, padding: "0 6px", flexShrink: 0 }}>
        <span style={{ ...tabS(true), cursor: "default" }}>Aperçu</span>
        <span style={{ flex: 1 }} />
        {Object.keys(DEVICES).map((d) => <button key={d} data-testid={`preview-device-${d.toLowerCase()}`} onClick={() => setDevice(d)} style={tabS(device === d)}>{d}</button>)}
      </div>
      <div style={{ flex: 1, overflow: "auto", display: "grid", placeItems: "center", padding: 12, background: "radial-gradient(ellipse at top, rgba(46,226,123,0.04), transparent)" }}>
        <div style={{ width: DEVICES[device], maxWidth: "100%", background: "#0A0F0C", border: `2px solid rgba(139,163,148,0.3)`, borderRadius: device === "Mobile" ? 22 : 14, overflow: "hidden", boxShadow: "0 18px 40px rgba(0,0,0,0.5)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 14px", fontSize: 10, fontWeight: 800, color: ST.text }}><span>9:41</span><span>▮▮ ⚡</span></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px" }}>
            <ArrowLeft size={14} color={ST.text} />
            <span style={{ flex: 1, textAlign: "center", fontSize: 12.5, fontWeight: 900, color: ST.text }}>AYA {build?.plan?.title ? build.plan.title.split(" ").slice(0, 2).join(" ") : "Transport"}</span>
            <Search size={13} color={ST.text} /><Menu size={13} color={ST.text} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, padding: "6px 12px" }}>
            {MAIN.map(({ l, c, I }) => (
              <div key={l} style={{ background: `${c}18`, border: `1px solid ${c}40`, borderRadius: 11, padding: "12px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                <I size={17} color={c} /><span style={{ fontSize: 9.5, fontWeight: 800, color: ST.text }}>{l}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: "4px 12px" }}>
            <div data-testid="preview-new-component" style={{ background: showNew ? "rgba(46,226,123,0.14)" : "rgba(56,189,248,0.1)", border: `1.5px solid ${showNew ? ST.green : "rgba(56,189,248,0.35)"}`, borderRadius: 12, padding: "11px 12px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, boxShadow: showNew ? "0 0 18px rgba(46,226,123,0.25)" : "none", transition: "all 0.5s ease" }}>
              <Users size={15} color={showNew ? ST.green : ST.blue} />
              <span style={{ fontSize: 11.5, fontWeight: 900, color: ST.text }}>{showNew ? label : "Transport privé"}</span>
              {showNew && <span style={chip(ST.green)}>nouveau</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, padding: "6px 12px 14px", overflow: "hidden" }}>
            {SEC.map(({ l, I }) => (
              <div key={l} style={{ flex: 1, background: ST.card, border: `1px solid ${ST.border2}`, borderRadius: 10, padding: "9px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <I size={13} color={ST.muted} /><span style={{ fontSize: 8.5, fontWeight: 700, color: ST.muted }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const VIDS = [
  { src: "/studio/vid_car.jpg", t: "0:20" }, { src: "/studio/vid_driver.jpg", t: "0:20" }, { src: "/studio/vid_city.jpg", t: "0:16" },
];
const MTABS = ["Design", "Composants", "Assets", "Vidéos", "Plus"];
const sel = { background: ST.soft, border: `1px solid ${ST.border2}`, borderRadius: 8, color: ST.text, fontSize: 11, padding: "5px 7px", outline: "none" };

export function MediaStudioPanel({ build, full, prodMode }) {
  const [tab, setTab] = useState("Vidéos");
  const [prompt, setPrompt] = useState("");
  const [prog, setProg] = useState(0);
  const [done, setDone] = useState(false);
  const timer = useRef(null);
  const gen = () => {
    if (timer.current) return;
    setDone(false); setProg(1);
    timer.current = setInterval(() => setProg((p) => {
      if (p >= 100) { clearInterval(timer.current); timer.current = null; setDone(true); return 0; }
      return p + 4;
    }), 180);
  };
  return (
    <section style={{ ...PNL, flex: 1 }} data-testid="studio-media-panel">
      <div style={{ display: "flex", borderBottom: `1px solid ${ST.border}`, padding: "0 6px", flexShrink: 0, overflowX: "auto" }}>
        {MTABS.map((t) => <button key={t} onClick={() => setTab(t)} style={tabS(tab === t)}>{t}</button>)}
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
        {tab !== "Vidéos" ? (
          <div style={{ color: ST.muted, fontSize: 12 }}>{tab} — bibliothèque gérée par Media AI. Sélectionnez « Vidéos » pour la génération.</div>
        ) : prodMode ? (
          <div data-testid="media-prod-notice" style={{ color: ST.muted, fontSize: 12, lineHeight: 1.6 }}>
            <span style={chip(ST.red)}>PRODUCTION</span>
            <div style={{ marginTop: 8 }}>La génération simulée est désactivée. En attente de l'intégration vidéo réelle (fal.ai).</div>
          </div>
        ) : (
          <>
            <textarea data-testid="media-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
              placeholder={`Crée une vidéo TikTok de 20 secondes pour promouvoir ${build?.plan?.title || "AYA Transport"}.`}
              style={{ width: "100%", boxSizing: "border-box", background: ST.soft, border: `1px solid ${ST.border}`, borderRadius: 10, padding: "9px 11px", color: ST.text, fontSize: 11.5, outline: "none", resize: "none", fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 7, margin: "8px 0" }}>
              <label style={{ flex: 1, fontSize: 9.5, color: ST.muted, fontWeight: 800 }}>Format<br /><select style={{ ...sel, width: "100%", marginTop: 3 }}><option>Vertical (9:16)</option><option>Carré (1:1)</option><option>Horizontal (16:9)</option></select></label>
              <label style={{ flex: 0.7, fontSize: 9.5, color: ST.muted, fontWeight: 800 }}>Durée<br /><select style={{ ...sel, width: "100%", marginTop: 3 }}><option>20s</option><option>10s</option><option>30s</option></select></label>
              <label style={{ flex: 0.7, fontSize: 9.5, color: ST.muted, fontWeight: 800 }}>Style<br /><select style={{ ...sel, width: "100%", marginTop: 3 }}><option>AYA</option><option>Ciné</option><option>UGC</option></select></label>
            </div>
            <button data-testid="media-generate" onClick={gen} style={{ width: "100%", padding: "9px 0", borderRadius: 10, border: "none", background: prog > 0 ? "rgba(46,226,123,0.15)" : "linear-gradient(180deg,#3BE07A,#1DB954)", color: prog > 0 ? ST.green : "#06210F", fontSize: 12, fontWeight: 900, cursor: "pointer" }}>
              {prog > 0 ? `Media AI génère… ${prog}%` : <><Clapperboard size={13} style={{ verticalAlign: -2, marginRight: 5 }} />Générer la vidéo avec AYA</>}
            </button>
            <div style={{ display: "grid", gridTemplateColumns: full ? "repeat(auto-fill,minmax(150px,1fr))" : "repeat(3,1fr)", gap: 7, marginTop: 10 }} data-testid="media-thumbnails">
              {VIDS.map((v, i) => (
                <div key={i} style={{ position: "relative", borderRadius: 10, overflow: "hidden", aspectRatio: "9/14", opacity: done || prog === 0 ? 1 : 0.3, transition: "opacity 0.4s", border: `1px solid ${ST.border2}` }}>
                  <img src={v.src} alt="vidéo générée" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}><Play size={18} color="#fff" fill="#ffffffcc" /></div>
                  <span style={{ position: "absolute", bottom: 5, right: 6, fontSize: 9, fontWeight: 900, color: "#fff", background: "rgba(0,0,0,0.6)", borderRadius: 6, padding: "1px 6px" }}>{v.t}</span>
                  <span style={{ position: "absolute", top: 5, left: 6, ...chip(ST.gold), background: "rgba(0,0,0,0.55)" }}>SIMULÉ</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
